import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, PieChart, Loader2, Wallet, AlertCircle, CheckCircle2, Hourglass, Phone, Mail } from 'lucide-react';
import { useStatsBlockGuard } from '../hooks/useStatsBlockGuard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtCA = (n) => `${(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;

export default function StatistiquesTresorerie() {
    useStatsBlockGuard('stats_tresorerie');
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API_URL}/api/statistiques/tresorerie`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    const totals = data?.totals || {};
    const impayes = data?.impayes_top || [];
    const tauxRecouvrement = totals.total_facture > 0 ? Math.round((totals.total_encaisse / totals.total_facture) * 1000) / 10 : 0;

    const KPI = ({ label, value, icon: Icon, color, hint }) => (
        <Card className="shadow-sm">
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase text-gray-500 tracking-wide">{label}</p>
                        <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
                        {hint ? <p className="text-xs text-gray-400 mt-0.5">{hint}</p> : null}
                    </div>
                    <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}1A` }}>
                        <Icon className="h-5 w-5" style={{ color }} />
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    const ageBadge = (j) => {
        if (j == null) return <Badge variant="outline">-</Badge>;
        if (j > 60) return <Badge className="bg-red-100 text-red-700 border-0">{j}j</Badge>;
        if (j > 30) return <Badge className="bg-amber-100 text-amber-700 border-0">{j}j</Badge>;
        return <Badge className="bg-emerald-100 text-emerald-700 border-0">{j}j</Badge>;
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fadeIn" data-testid="tresorerie-page">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/statistiques')} className="h-8 w-8 p-0">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                        <PieChart className="h-5 w-5 text-red-500" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Trésorerie & impayés</h1>
                        <p className="text-gray-500 mt-0.5 text-sm">Suivi des paiements et recouvrement</p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#1B365D]" /></div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPI label="Facturé" value={fmtCA(totals.total_facture)} icon={Wallet} color="#1B365D" hint={`${totals.nb_total || 0} factures`} />
                            <KPI label="Encaissé" value={fmtCA(totals.total_encaisse)} icon={CheckCircle2} color="#10B981" hint={`recouvrement ${tauxRecouvrement}%`} />
                            <KPI label="Impayé" value={fmtCA(totals.total_impaye)} icon={AlertCircle} color="#EF4444" hint={`${totals.pct_impayes || 0}% en défaut`} />
                            <KPI label="Délai moyen" value={totals.delai_moyen_paiement_jours != null ? `${totals.delai_moyen_paiement_jours} j` : '—'} icon={Hourglass} color="#C9A962" hint="paiement complet" />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <Card className="shadow-sm">
                                <CardContent className="p-5 flex items-center justify-between">
                                    <p className="text-sm text-gray-500">Totalement payées</p>
                                    <p className="text-2xl font-bold text-emerald-600">{totals.nb_paye || 0}</p>
                                </CardContent>
                            </Card>
                            <Card className="shadow-sm">
                                <CardContent className="p-5 flex items-center justify-between">
                                    <p className="text-sm text-gray-500">Partiellement payées</p>
                                    <p className="text-2xl font-bold text-amber-500">{totals.nb_partiel || 0}</p>
                                </CardContent>
                            </Card>
                            <Card className="shadow-sm">
                                <CardContent className="p-5 flex items-center justify-between">
                                    <p className="text-sm text-gray-500">Impayées</p>
                                    <p className="text-2xl font-bold text-red-500">{totals.nb_impaye || 0}</p>
                                </CardContent>
                            </Card>
                        </div>

                        <Card className="shadow-sm">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-lg flex items-center gap-2 text-[#1B365D]">
                                    <AlertCircle className="w-5 h-5 text-red-500" /> Top impayés (par ancienneté)
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                {impayes.length === 0 ? (
                                    <p className="text-center text-gray-400 py-8">Aucun impayé 🎉</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm" data-testid="tresorerie-table">
                                            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                                                <tr>
                                                    <th className="px-4 py-3 text-left">N° / Client</th>
                                                    <th className="px-4 py-3 text-right">Montant</th>
                                                    <th className="px-4 py-3 text-right">Payé</th>
                                                    <th className="px-4 py-3 text-right">Solde</th>
                                                    <th className="px-4 py-3 text-center">Ancienneté</th>
                                                    <th className="px-4 py-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {impayes.map((f) => (
                                                    <tr key={f.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3">
                                                            <p className="font-medium text-[#1B365D] cursor-pointer hover:underline" onClick={() => navigate(`/clients/${f.client_id}`)}>{f.client_nom}</p>
                                                            <p className="text-xs text-gray-400">{f.numero}</p>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">{fmtCA(f.prix_ttc)}</td>
                                                        <td className="px-4 py-3 text-right text-emerald-600">{fmtCA(f.paye)}</td>
                                                        <td className="px-4 py-3 text-right font-bold text-red-500">{fmtCA(f.solde)}</td>
                                                        <td className="px-4 py-3 text-center">{ageBadge(f.jours_ecoules)}</td>
                                                        <td className="px-4 py-3 text-right">
                                                            <div className="flex items-center justify-end gap-2">
                                                                {f.client_telephone && (
                                                                    <a href={`tel:${f.client_telephone}`} className="text-gray-400 hover:text-emerald-600" title="Appeler"><Phone className="w-4 h-4" /></a>
                                                                )}
                                                                {f.client_email && (
                                                                    <a href={`mailto:${f.client_email}?subject=Relance facture ${f.numero}`} className="text-gray-400 hover:text-blue-600" title="Email"><Mail className="w-4 h-4" /></a>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </Layout>
    );
}
